/**
 * DowngradeWarningModal Component
 * Confirmation dialog shown before opening Stripe billing portal for paid users.
 * Lists features that will be lost on downgrade/cancellation.
 */

import { AlertTriangle } from 'lucide-react';
import type { ReactElement } from 'react';

interface DowngradeWarningModalProps {
  isPremium: boolean;
  isPro: boolean;
  isElite?: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
  portalLoading: boolean;
}

export function DowngradeWarningModal({
  isPremium,
  isPro,
  isElite,
  onConfirm,
  onDismiss,
  portalLoading,
}: DowngradeWarningModalProps): ReactElement {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onDismiss} />

      {/* Modal */}
      <div className="relative bg-theme-surface border border-theme-border rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full flex items-center justify-center bg-theme-warning/20">
            <AlertTriangle className="w-8 h-8 text-theme-warning" />
          </div>
        </div>

        {/* Title */}
        <h3 className="text-xl font-bold text-center mb-2 text-theme-warning">
          Downgrade Your Plan?
        </h3>

        {/* Message */}
        <p className="text-theme-text-secondary text-center text-sm mb-4">
          If you cancel or downgrade, you will lose access to:
        </p>

        {/* Feature loss list */}
        <ul className="text-sm space-y-1.5 mb-4 text-theme-text">
          {isElite && (
            <>
              <li className="flex items-center gap-2">
                <span className="text-theme-danger">&#x2717;</span>
                Sharp money signals & +EV badges
              </li>
              <li className="flex items-center gap-2">
                <span className="text-theme-danger">&#x2717;</span>
                Middles & sharp signal alerts
              </li>
              <li className="flex items-center gap-2">
                <span className="text-theme-danger">&#x2717;</span>
                Step-by-step prediction math
              </li>
            </>
          )}
          {(isPremium || isElite) && (
            <>
              <li className="flex items-center gap-2">
                <span className="text-theme-danger">&#x2717;</span>
                Custom themes (resets to default)
              </li>
              <li className="flex items-center gap-2">
                <span className="text-theme-danger">&#x2717;</span>
                Detailed AI analysis mode (resets to standard)
              </li>
            </>
          )}
          {(isPro || isPremium || isElite) && (
            <>
              <li className="flex items-center gap-2">
                <span className="text-theme-danger">&#x2717;</span>
                Follower permissions (all disabled)
              </li>
              <li className="flex items-center gap-2">
                <span className="text-theme-danger">&#x2717;</span>
                Social sharing features
              </li>
              <li className="flex items-center gap-2">
                <span className="text-theme-danger">&#x2717;</span>
                Best line finder
              </li>
              <li className="flex items-center gap-2">
                <span className="text-theme-danger">&#x2717;</span>
                Daily quota adjusts to new tier limits
              </li>
            </>
          )}
        </ul>

        <p className="text-xs text-theme-text-tertiary text-center mb-6">
          Changes take effect at the end of your current billing period. You&apos;ll keep your data
          and performance history.
        </p>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onDismiss}
            className="flex-1 py-2.5 rounded-lg bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill font-medium transition"
          >
            Keep My Plan
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={portalLoading}
            className="flex-1 py-2.5 rounded-lg bg-theme-surface-hover hover:bg-theme-surface-active text-theme-text font-medium transition disabled:text-theme-text-tertiary disabled:cursor-not-allowed"
          >
            {portalLoading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-theme-text border-t-transparent" />
                Opening...
              </span>
            ) : (
              'Continue to Billing'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
